// Supervisor check: finished jobs wait for ✓ OK or ✕ Redo (with what needs fixing).
import { useState } from 'react';
import { api, useApi, navigate, currentMe, fmtTime, type Row } from '../lib';
import { Badge, Button, Modal, Form, TextArea, Loading, Empty, Panel, useToast } from '../ui';

export const canCheck = () => { const me = currentMe(); return !!me && (me.user.role === 'owner' || !!me.user.supervisor); };

/** The job's check state, as a badge. */
export function CheckBadge({ job }: { job: Row }) {
  if (job.check_status === 'waiting') return <Badge tone="amber">Waiting for check</Badge>;
  if (job.check_status === 'ok') return <span className="check-ok" title={job.checked_at ? fmtTime(job.checked_at) : ''}>✓ Checked{job.checked_by_name ? ` by ${job.checked_by_name}` : ''}{job.check_note ? ` — ${job.check_note}` : ''}</span>;
  if (job.check_status === 'redo') return <span className="check-redo">↺ Redo{job.checked_by_name ? ` (${job.checked_by_name})` : ''}: {job.check_note}</span>;
  return null;
}

/** ✓ OK / ✕ Redo buttons for the supervisor. */
export function CheckButtons({ job, onDone, big }: { job: Row; onDone: () => void; big?: boolean }) {
  const [redo, setRedo] = useState(false);
  const toast = useToast();
  if (job.check_status !== 'waiting' || job.status !== 'done' || !canCheck()) return null;
  return (
    <span className={'check-btns' + (big ? ' big' : '')}>
      <Button kind="primary" className={big ? '' : 'small'} onClick={async () => { try { await api(`/api/jobs/${job.id}/check`, { body: { ok: true } }); toast('Checked ✓'); onDone(); } catch (e) { toast((e as Error).message, 'err'); } }}>✓ Checked OK</Button>
      <Button kind="danger" className={big ? '' : 'small'} onClick={() => setRedo(true)}>✕ Redo</Button>
      {redo && <Modal title={`Redo — ${job.title}`} onClose={() => setRedo(false)}>
        <Form submitLabel="Send back to the mechanic" onCancel={() => setRedo(false)} onSubmit={async v => {
          await api(`/api/jobs/${job.id}/check`, { body: { ok: false, note: v.note } }); setRedo(false); toast('Sent back — the mechanic gets a notification'); onDone();
        }}>
          <TextArea label="What needs fixing?" name="note" required placeholder="e.g. Brake chamber still leaking at the fitting" />
        </Form>
      </Modal>}
    </span>
  );
}

/** Every job waiting for the supervisor (office page / phone tab). */
export function ChecksList({ phone }: { phone?: boolean }) {
  const { data, reload } = useApi<Row[]>('/api/checks');
  if (!data) return <Loading />;
  if (!data.length) return <Empty>Nothing waiting for a check.</Empty>;
  const body = (
    <ul className="check-list">{data.map(j => (
      <li key={j.id}>
        <button type="button" className="linkish" onClick={() => navigate((phone ? '/floor/jobs/' : '/work-orders/') + j.work_order_id)}>
          <b>{j.title}</b><small className="muted"> · {j.unit ? '#' + j.unit + ' · ' : ''}{j.number}{j.customer_name ? ' · ' + j.customer_name : ''}</small></button>
        <small className="muted">Done by {j.mechanic_name || '—'}{j.done_at ? ` · ${fmtTime(j.done_at)}` : ''}</small>
        <CheckButtons job={{ ...j, status: 'done', check_status: 'waiting' }} onDone={reload} big={phone} />
      </li>))}</ul>
  );
  return phone ? body : <Panel>{body}</Panel>;
}

export function ChecksPage() {
  return <>
    <div className="page-head"><div><h1>Jobs to check</h1><p>Finished jobs waiting for the supervisor. ✓ OK finishes them; Redo sends them back to the mechanic with your note.</p></div></div>
    <ChecksList />
  </>;
}
